import React from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material'
import { Check, Email, Lock, Person, Phone } from '@mui/icons-material'
import PrivacyNotice from '../../components/PrivacyNotice'
import TermsAndConditions from '../../components/TermsAndConditions'

interface PasswordRules {
  length: boolean
  uppercase: boolean
  lowercase: boolean
  number: boolean
  special: boolean
}

interface CreateBusinessFormProps {
  formData: {
    managerName: string
    username: string
    phone: string
    password: string
    confirmPassword: string
    termsAgreed: boolean
  }
  passwordRules: PasswordRules
  passwordValid: boolean
  usernameValid: boolean
  emailValid: boolean
  phoneValid: boolean
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  switchMode: (mode: string) => void
}

const CreateBusinessForm: React.FC<CreateBusinessFormProps> = ({
  formData,
  passwordRules,
  passwordValid,
  usernameValid,
  emailValid,
  phoneValid,
  handleChange,
  switchMode,
}) => {
  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Create Account
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Set up your account and start managing your schedules
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Manager Name */}
        <TextField
          fullWidth
          label="Your Full Name"
          name="managerName"
          value={formData.managerName}
          onChange={handleChange}
          variant="outlined"
          placeholder="Enter your full name"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Contact Info */}
        <Box>
          <Typography variant="body2" fontWeight={600}>
            Contact Info
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Enter at least one — email or phone number.
          </Typography>
        </Box>

        {/* Email */}
        <TextField
          fullWidth
          label="Email Address"
          name="username"
          type="email"
          value={formData.username}
          onChange={handleChange}
          variant="outlined"
          placeholder="stafflows@company.com"
          error={formData.username !== '' && !emailValid}
          helperText={formData.username !== '' && !emailValid ? 'Enter a valid email address.' : ''}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Phone */}
        <TextField
          fullWidth
          label="Phone Number"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleChange}
          variant="outlined"
          placeholder="1234567890"
          error={formData.phone !== '' && !phoneValid}
          helperText={formData.phone !== '' && !phoneValid ? 'Enter a valid 10-digit phone number.' : ''}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Phone color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Password Rules */}
        <Box aria-live="polite" sx={{ mt: 0.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Check sx={{ color: passwordRules.length ? 'success.main' : 'text.disabled' }} />
              <Typography variant="caption" color="text.primary">
                At least 8 characters
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Check sx={{ color: passwordRules.uppercase ? 'success.main' : 'text.disabled' }} />
              <Typography variant="caption" color="text.primary">
                Uppercase letter (A-Z)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Check sx={{ color: passwordRules.lowercase ? 'success.main' : 'text.disabled' }} />
              <Typography variant="caption" color="text.primary">
                Lowercase letter (a-z)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Check sx={{ color: passwordRules.number ? 'success.main' : 'text.disabled' }} />
              <Typography variant="caption" color="text.primary">
                Number (0-9)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Check sx={{ color: passwordRules.special ? 'success.main' : 'text.disabled' }} />
              <Typography variant="caption" color="text.primary">
                Special character (e.g. !@#$%)
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Password */}
        <TextField
          fullWidth
          label="Password"
          name="password"
          type="password"
          value={formData.password}
          onChange={handleChange}
          variant="outlined"
          placeholder="••••••••"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock color="action" />
              </InputAdornment>
            ),
          }}
          helperText="Password must be at least 8 characters long and include uppercase letters, lowercase letters, numbers, and special characters."
        />

        {/* Confirm Password */}
        <TextField
          fullWidth
          label="Confirm Password"
          name="confirmPassword"
          type="password"
          value={formData.confirmPassword}
          onChange={handleChange}
          variant="outlined"
          placeholder="••••••••"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock color="action" />
              </InputAdornment>
            ),
          }}
          helperText={
            formData.password === ''
              ? ''
              : formData.password === formData.confirmPassword
              ? 'Passwords match.'
              : 'Passwords do not match.'
          }
        />

        {/* Terms Agreement */}
        <FormControlLabel
          control={
            <Checkbox color="primary" checked={formData.termsAgreed} onChange={handleChange} />
          }
          label={
            <Typography variant="body2">
              I agree to the <TermsAndConditions color="primary" /> and{' '}
              <PrivacyNotice color="primary" />
            </Typography>
          }
        />

        {/* Create Business Button */}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={
            !formData.managerName ||
            (formData.username === '' && formData.phone === '') ||
            !formData.password ||
            !formData.confirmPassword ||
            formData.password !== formData.confirmPassword ||
            !passwordValid ||
            !usernameValid ||
            !formData.termsAgreed
          }
          sx={{
            py: 1.5,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            boxShadow: 3,
          }}
        >
          Create Account
        </Button>

        {/* Back to Login */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" component="span">
            Already have an account?
          </Typography>
          <Link
            component="button"
            type="button"
            onClick={() => switchMode('login')}
            variant="body2"
            sx={{ ml: 0.5, fontWeight: 600 }}
            underline="hover"
          >
            Sign In
          </Link>
        </Box>
      </Box>
    </>
  )
}

export default CreateBusinessForm
